import {SierraCanvas} from '../../classes';

/**
 * @name isFontAvailable
 * @description Checks if a font is available by one of two methods, depending on browser
 * support
 */
export const isFontAvailable = (fontName: string): boolean => {
  if (fontFaceSetCheckIsReliable()) {
    return testFontFaceSetCheck(fontName);
  }

  return testFontIsAvailableViaCanvas(fontName);
};

/**
 * @name testFontFaceSetCheck
 * @description Uses FontFaceSet.check() to test if a font is available. The reliability of
 * this method varies per browser, so fontFaceSetCheckIsReliable can be used to
 * test if this will return the desired result. If not, recommend using
 * isFontAvailable
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/FontFaceSet/check
 */
const testFontFaceSetCheck = (fontName: string): boolean => {
  try {
    return document.fonts.check(`12px "${fontName}"`);
    // eslint-disable-next-line
  } catch (e) {
    // document.fonts.check throws an error if the font string isn't properly
    // formatted, but we can ignore that
  }

  return false;
};

/**
 * @name fontFaceSetCheckIsReliable
 * @description Tests FontFaceSet.check() to see if it returns false for a font that is
 * known not to exist. Some browsers will return true for unrecognised fonts,
 * rendering the FontFaceSet.check method useless for our intended use
 */
const fontFaceSetCheckIsReliable = (): boolean =>
  !testFontFaceSetCheck('thisFontDoesNotExist');

/**
 * @name testFontIsAvailableViaCanvas
 * @description Detects if a font is available by checking the width of a string of characters
 * in that font twice and comparing the widths
 */
const testFontIsAvailableViaCanvas = (fontName: string): boolean => {
  const cnv = SierraCanvas.global;

  // if the font isn't available, this will default to serif
  cnv.context.font = `12px "${fontName}", serif`;
  const width1 = cnv.measureText('abcdefghijklmnopqrstuvwxyz').width;

  // if the font isn't available, this will default to sans-serif
  cnv.context.font = `12px "${fontName}", sans-serif`;
  const width2 = cnv.measureText('abcdefghijklmnopqrstuvwxyz').width;

  // if the width in both instances is the same, it didn't fallback to serif
  // or sans-serif and the font is available
  return width1 === width2;
};
